import type {AxiosError} from "axios";

import type {ResidentClaimApiResponse} from "../types";

export const CLAIM_ERROR_CODES = {
  VALIDATION_FAILED: "validation_failed",
  CONFLICT: "conflict",
  SERVICE_UNAVAILABLE: "service_unavailable",
  TOO_MANY_REQUESTS: "too_many_requests",
} as const;

export const CLAIM_INVALID_CODE_MESSAGE =
  "This claim code is invalid, expired, or has already been used.";

type ClaimErrorBody = ResidentClaimApiResponse<unknown>;

function getClaimErrorBody(error: unknown): ClaimErrorBody | undefined {
  if (!error || typeof error !== "object" || !("response" in error)) return undefined;
  return (error as AxiosError<ClaimErrorBody>).response?.data;
}

export function getClaimErrorCode(error: unknown): string | undefined {
  return getClaimErrorBody(error)?.error_code;
}

export function getClaimErrorMessage(error: unknown, fallback: string): string {
  return getClaimErrorBody(error)?.message ?? fallback;
}

export function getClaimFieldErrors(error: unknown): Record<string, string[]> | undefined {
  return getClaimErrorBody(error)?.errors;
}

export function isClaimDisabledError(error: unknown): boolean {
  return getClaimErrorCode(error) === CLAIM_ERROR_CODES.SERVICE_UNAVAILABLE;
}

export function isClaimRateLimitedError(error: unknown): boolean {
  return getClaimErrorCode(error) === CLAIM_ERROR_CODES.TOO_MANY_REQUESTS;
}

export function isClaimExpiredVerifyError(error: unknown): boolean {
  const axiosError = error as AxiosError<ClaimErrorBody>;
  return (
    axiosError.response?.status === 409 &&
    getClaimErrorCode(error) === CLAIM_ERROR_CODES.CONFLICT
  );
}

export function isClaimInvalidCodeError(error: unknown): boolean {
  const message = getClaimErrorMessage(error, "");
  return message === CLAIM_INVALID_CODE_MESSAGE;
}
